// Basic text helper functions for line-angle utilities

export function getAnchor(_text: string): string {
  // Simple implementation - return 'start' for most cases
  return 'start';
}

export function calculateWidth(text: string): number {
  // Rough approximation of text width
  return text.length * 8;
}

/**
 * Calculate x-offset for atom text to center the non-hydrogen part
 *
 * Examples:
 * - "H₂O" → offset left to center "O"
 * - "OH" → offset right to center "O"
 * - "C" → no offset (0)
 *
 * @param text - Display symbol (e.g., "H₂O", "OH", "CH₃")
 * @param fontSize - Font size for accurate width calculation
 * @returns x-offset in SVG units
 */
export function calculateHydrogenOffset(text: string, fontSize: number = 2.75): number {
  // No offset for single character or paper carbons
  if (text.length === 1) {
    return 0;
  }

  // Character width approximation based on font size
  // Rough estimates: uppercase = 0.65 * fontSize, lowercase/numbers = 0.55 * fontSize
  const charWidth = (char: string) => {
    if (char >= 'A' && char <= 'Z') return 0.65 * fontSize;
    if (char >= '0' && char <= '9') return 0.55 * fontSize;
    if (char >= 'a' && char <= 'z') return 0.55 * fontSize;
    return 0.5 * fontSize;
  };

  // Check if hydrogen is on LEFT (text starts with H)
  if (text[0] === 'H') {
    // Calculate width of hydrogen part (H, H2, H3, etc.)
    let hWidth = charWidth('H');
    let idx = 1;

    // Include subscript numbers
    while (idx < text.length && text[idx] >= '0' && text[idx] <= '9') {
      hWidth += charWidth(text[idx]);
      idx++;
    }

    // Include subscript indicators like ₂, ₃
    if (idx < text.length && '₀₁₂₃₄₅₆₇₈₉'.includes(text[idx])) {
      hWidth += charWidth('0'); // Treat subscript as regular number width
      idx++;
    }

    // Shift LEFT by half the hydrogen width to center the element
    return -(hWidth / 2);
  }

  // Check if hydrogen is on RIGHT (text ends with H or number+H)
  if (text[text.length - 1] === 'H' ||
      (text.length > 1 && text[text.length - 2] === 'H')) {
    // Calculate width of hydrogen part from the end
    let hWidth = charWidth('H');
    let idx = text.length - 2;

    // Include preceding numbers (e.g., "3" in "CH3")
    while (idx >= 0 && text[idx] >= '0' && text[idx] <= '9') {
      hWidth += charWidth(text[idx]);
      idx--;
    }

    // Include subscript indicators
    if (idx >= 0 && '₀₁₂₃₄₅₆₇₈₉'.includes(text[idx])) {
      hWidth += charWidth('0');
      idx--;
    }

    // Shift RIGHT by half the hydrogen width to center the element
    return hWidth / 2;
  }

  // No hydrogen in symbol, no offset needed
  return 0;
}

/**
 * Calculate x-offset for group text alignment based on bond direction
 *
 * Groups (like TBDMS, SnBu3) should be aligned based on where bonds connect:
 * - Bond on RIGHT → LEFT align text (offset negative)
 * - Bond on LEFT → RIGHT align text (offset positive)
 * - Default → LEFT align
 *
 * The offset leaves a small visual gap (approximately half a letter width)
 * between the text edge and the bond attachment point for better readability.
 *
 * @param text - Group symbol (e.g., "TBDMS", "SnBu3")
 * @param bondDirection - Direction of bond: 'left' | 'right' | 'default'
 * @param fontSize - Font size for width calculation
 * @returns x-offset in SVG units for text alignment
 */
export function calculateGroupOffset(
  text: string,
  bondDirection: 'left' | 'right' | 'default',
  fontSize: number = 2.75
): number {
  // Character width approximation
  const charWidth = (char: string) => {
    if (char >= 'A' && char <= 'Z') return 0.65 * fontSize;
    if (char >= '0' && char <= '9') return 0.55 * fontSize;
    if (char >= 'a' && char <= 'z') return 0.55 * fontSize;
    return 0.5 * fontSize;
  };

  // Calculate total text width
  let totalWidth = 0;
  for (let i = 0; i < text.length; i++) {
    totalWidth += charWidth(text[i]);
  }

  // Visual gap between text and bond (approximately half an uppercase letter)
  const visualGap = 0.65 * fontSize / 2;

  // Determine offset based on bond direction
  // Bond on RIGHT → LEFT align (shift left by half width, minus gap)
  // Bond on LEFT → RIGHT align (shift right by half width, minus gap)
  // Default → LEFT align (shift left by half width, minus gap)
  if (bondDirection === 'left') {
    // Bond on left → right align text → shift right, but leave gap on left
    return (totalWidth / 2) - visualGap;
  } else {
    // Bond on right or default → left align text → shift left, but leave gap on right
    return -(totalWidth / 2) + visualGap;
  }
}

export function getOffsetX(text: string, fontSize?: number): number {
  // Use hydrogen offset calculation to center non-hydrogen part
  return calculateHydrogenOffset(text, fontSize);
}

export function getOffsetY(_text: string): number {
  // Simple offset calculation
  return 0;
}

export function getAtomLabelHeight(_text: string): number {
  // Standard height for atom labels
  return 12;
}